import React, { useMemo, useState } from 'react'
import './ManageUser.css'

import { Box, Checkbox, FormControlLabel, Grid, InputAdornment, TextField, Typography } from '@mui/material'

import { useNavigate } from 'react-router-dom';

import DataTable from '../../components/DataTable/DataTable'
import OptionButton from '../../components/OptionButton/OptionButton';
import DeleteButton from '../../components/TableButton/DeleteButton';
import EditButton from '../../components/TableButton/EditButton';
import ExcelButton from '../../components/ExcelButton/ExcelButton';
import StatusButton from '../../components/TableButton/StatusButton';

import { userServices } from '../../services/userServices';

import useDataTable from '../../hooks/useDataTable';
import useUser from '../../hooks/useUser';

const columns = [
    {
        field: 'row_num',
        headerName: 'ลำดับ',
        headerAlign: 'center',
        align: 'center',
        sortable: false,
    },
    {
        field: 'username',
        headerName: 'ชื่อผู้ใช้',
        headerAlign: 'center',
        align: 'center',
        sortable: false,
    },
    {
        field: 'fullname',
        headerName: 'ชื่อ-นามสกุล',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        sortable: false,
        valueGetter: (param) => `${param.row.first_name || ''} ${param.row.last_name || ''}`
    },
    {
        field: 'group_name',
        headerName: 'กลุ่มผู้ใช้งาน',
        headerAlign: 'center',
        align: 'center',
        sortable: false,
    },
    {
        field: 'email',
        headerName: 'อี-เมล',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        sortable: false,
    },
    {
        field: 'table_status',
        headerName: 'สถานะ',
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        renderCell: (param) => <StatusButton data={param.row} service={userServices.updateStatusById} />
    },
    {
        field: 'edit',
        headerName: 'แก้ไข',
        headerAlign: 'center',
        type: 'actions',
        sortable: false,
        renderCell: (param) => <EditButton id={param.row.id} formURL="manage-user-form" />
    },
    {
        field: 'delete',
        type: 'actions',
        sortable: false,
        renderCell: (param) => <DeleteButton id={param.row.id} service={userServices.deleteUserById} />
    },
];

const initFilters = {
    search: '',
    role: [],
    dateFrom: '',
    dateTo: '',
}

function ManageUser() {
    const navigate = useNavigate();

    const { paginationModel, resetPage } = useDataTable();

    const [filters, setFilters] = useState(initFilters);
    const [paginationfilters, setPaginationFilters] = useState(initFilters);
    const [checkedAll, setCheckedAll] = useState(false);

    const userRoles = useMemo(() => {
        return [
            {
                text: 'USER',
                label: 'ผู้ใช้งาน',
                checked: false,
            },
            {
                text: 'ADMIN',
                label: 'ผู้ดูแลระบบ',
                checked: false,
            },
        ];
    }, []);

    const [roleList, setRoleList] = useState(userRoles);

    const { userList: rows, rowCount } = useUser(
        { ...paginationfilters, ...paginationModel },
        [paginationModel.page, paginationfilters]
    );

    const handleAddClick = () => navigate('/dashboard/manage-user-form');

    const handleFilterChange = ({ target }) => {
        const { name, value, type, checked } = target;
        console.log(value);

        if (type === "checkbox") {
            setCheckedAll(false);
            setRoleList(prev => {
                const index = prev.findIndex(r => r.text === value);
                const newTargetType = prev[index];

                newTargetType.checked = checked;

                prev[index] = newTargetType;

                return [...prev]
            });
        }

        setFilters(prev => {
            return {
                ...prev,
                [name]: type === "checkbox" ? (
                    checked ? (
                        [...prev.role, value]
                    ) : prev.role.filter(r => r !== value)
                ) : value,
            }
        });
    }

    const handleCheckedAllChange = ({ target }) => {
        const checked = target.checked;
        const roleCheckedList = checked ? roleList.map(role => role.text) : [];

        setCheckedAll(checked);
        setRoleList(prev => prev.map(role => {
            return {
                ...role,
                checked,
            }
        }));
        setFilters(prev => {
            return {
                ...prev,
                role: roleCheckedList
            }
        })
    }

    const handleSearchClick = () => {
        console.log(filters);
        setPaginationFilters({ ...filters });
        resetPage();
    }

    const handleClearClick = () => {
        setCheckedAll(false);
        setRoleList(prev => prev.map(role => {
            return {
                ...role,
                checked: false,
            }
        }));
        setFilters({ ...initFilters });
        setPaginationFilters({ ...initFilters });
        resetPage();
    }

    return (
        <div className="manage-user">
            <Grid container className="filters" gap={1}>
                <Grid container item md={12} display="flex" alignItems="center">
                    <Grid item md={1}>
                        <Typography>ค้นหา</Typography>
                    </Grid>
                    <Grid item md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            name="search"
                            value={filters.search}
                            onChange={handleFilterChange}
                        />
                    </Grid>
                </Grid>
                <Grid container item md={12} display="flex" alignItems="center">
                    <Grid item md={1}>
                        <Typography>ระดับสิทธิ์</Typography>
                    </Grid>
                    <Grid item md={6}>
                        <FormControlLabel
                            control={<Checkbox />}
                            label="ทั้งหมด"
                            checked={checkedAll}
                            onChange={handleCheckedAllChange}
                        />
                        {roleList.map(role => (
                            <FormControlLabel
                                key={role.text}
                                control={<Checkbox />}
                                label={role.label}
                                name="role"
                                checked={role.checked}
                                value={role.text}
                                onChange={handleFilterChange}
                            />
                        ))}
                    </Grid>
                </Grid>
                <Grid container item md={12} display="flex" alignItems="center">
                    <Grid item md={1}>
                        <Typography>วันที่เข้าร่วม</Typography>
                    </Grid>
                    <Grid item md={3}>
                        <TextField
                            fullWidth
                            type="date"
                            size="small"
                            name="dateFrom"
                            value={filters.dateFrom}
                            onChange={handleFilterChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Box>From</Box>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item md={3}>
                        <TextField
                            fullWidth
                            type="date"
                            size="small"
                            name="dateTo"
                            value={filters.dateTo}
                            onChange={handleFilterChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Box>To</Box>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Grid container className="options">
                <Grid item md={1}></Grid>
                <Grid
                    item xs={12} md={6}
                    sx={{
                        '& button': { width: '25%' },
                        '& :not(:nth-of-type(1))': { ml: 2 }
                    }}
                >
                    <OptionButton
                        text='ค้นหา'
                        bgColor='var(--options-btn-bg-color-1)'
                        hoverColor='var(--options-btn-bg-hover-1)'
                        onClick={handleSearchClick}
                    />
                    <OptionButton
                        text='ล้าง'
                        bgColor='var(--options-btn-bg-color-2)'
                        hoverColor='var(--options-btn-bg-hover-2)'
                        textColor='gray'
                        onClick={handleClearClick}
                    />
                    <OptionButton
                        text='เพิ่ม'
                        bgColor='var(--options-btn-bg-color-1)'
                        hoverColor='var(--options-btn-bg-hover-1)'
                        onClick={handleAddClick}
                    />
                </Grid>

                <ExcelButton modelName="User" importCallback={handleClearClick} />
            </Grid>

            <DataTable
                columns={columns}
                rows={rows}
                rowCount={rowCount}
                pageProps={paginationModel}
            />
        </div>
    )
}

export default ManageUser